import React from 'react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  Legend,
  PolarAngleAxis,
  PolarRadiusAxis,
  Text,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { selectedPracticeArea } from '../appraisalComparisonCache';
import { useReactiveVar } from '@apollo/client';
import constants from '@shared-styles/constants.module.scss';
import { getPolarRadiusAxisLabel } from '../../shared/components/GraphLabels';
import { FILTER_TYPES_KEYS } from '../../../constants/appraisal-comparison-filter-types';

/**
 * @typedef {object} UnitComparisonRadarChartProps
 * @property {Array<*>} [data]
 * @property {string} [selectedDataKey]
 */

/**
 * @typedef {object} PolarAngleAxisTickProps
 * @property {{ value?: * }} payload
 * @property {number|string} x
 * @property {number|string} y
 * @property {number} [cx]
 * @property {number} [cy]
 */

/**
 * @param {UnitComparisonRadarChartProps} props
 */
const UnitComparisonRadarChart = props => {
  const { data, selectedDataKey = '' } = props;

  const selectedPracticeAreaId = useReactiveVar(selectedPracticeArea);

  const displayUnitRadar =
    selectedDataKey == radarNameKey.Industry.dataKey ||
    selectedDataKey == radarNameKey.Organization.dataKey;
  const displayIndRadar =
    selectedDataKey == radarNameKey.Industry.dataKey ||
    selectedDataKey == radarNameKey.OrganizationVsIndustry.dataKey;
  const displayOrgRadar =
    selectedDataKey == radarNameKey.Organization.dataKey ||
    selectedDataKey == radarNameKey.OrganizationVsIndustry.dataKey;

  const selectPracticeArea = point => {
    const practiceAreaId = data.filter(d => d.acronym == point.value)[0].id;
    selectedPracticeArea(practiceAreaId);
  };

  /**
   * @param {PolarAngleAxisTickProps} tickProps
   */
  const renderPolarAngleAxis = ({ payload, x, y, cx = 0, cy = 0, ...rest }) => {
    // "II" acronym is custom adjusted because it is too close to the graphic
    const distancePercentage = payload.value == 'II' ? 18 : 25;
    const practiceArea = data.find(d => d.acronym == payload.value);
    const tickX = Number(x);
    const tickY = Number(y);
    return (
      <Text
        {...rest}
        fill='currentColor'
        verticalAnchor='middle'
        y={tickY + (tickY - cy) / distancePercentage}
        x={tickX + (tickX - cx) / distancePercentage}
        fontWeight={
          practiceArea && practiceArea.ids.includes(selectedPracticeAreaId) ? '700' : '0'
        }>
        {payload.value}
      </Text>
    );
  };

  const renderPolarRadiusAxis = ({ payload, ...rest }) => {
    return <Text {...rest}>{payload.value + '%'}</Text>;
  };

  const tooltipLabelFormatter = label => {
    const tooltipLabel = data.filter(x => x.acronym === label);
    return tooltipLabel && tooltipLabel.length > 0 ? tooltipLabel[0].subject : label;
  };

  return (
    <ResponsiveContainer width='95%' height={400}>
      <RadarChart data={data}>
        {displayIndRadar && (
          <Radar
            name={radarNameKey.Industry.radarName}
            dataKey={radarNameKey.Industry.dataKey}
            fill={constants.industryColor}
            fillOpacity={0.65}
            stroke={constants.industryColor}
            strokeWidth='.15em'
            strokeOpacity={1}
          />
        )}

        {displayOrgRadar && (
          <Radar
            name={radarNameKey.Organization.radarName}
            dataKey={radarNameKey.Organization.dataKey}
            fill={constants.organizationColor}
            fillOpacity={0.65}
            stroke={constants.organizationColor}
            strokeWidth='.15em'
            strokeOpacity={1}
          />
        )}

        {displayUnitRadar && (
          <Radar
            name={radarNameKey.Unit.radarName}
            dataKey={radarNameKey.Unit.dataKey}
            fill={constants.unitColor}
            fillOpacity={0.65}
            stroke={constants.unitColor}
            strokeWidth='.15em'
            strokeOpacity={1}
          />
        )}

        <PolarGrid stroke='#000' strokeOpacity='.3' />
        <PolarAngleAxis
          dataKey='acronym'
          tick={renderPolarAngleAxis}
          onClick={point => selectPracticeArea(point)}
          style={{ cursor: 'pointer' }}
        />
        <Tooltip
          labelFormatter={label => tooltipLabelFormatter(label)}
          formatter={value => value + '%'}></Tooltip>

        <PolarRadiusAxis
          angle={10}
          domain={[0, 100]}
          stroke='#000'
          strokeOpacity='.5'
          tick={props => renderPolarRadiusAxis(props)}>
          {getPolarRadiusAxisLabel('Aggregate PA %', -15, 350)}
        </PolarRadiusAxis>
        <Legend align='center' />
      </RadarChart>
    </ResponsiveContainer>
  );
};

const radarNameKey = {
  Unit: {
    radarName: 'Unit',
    dataKey: FILTER_TYPES_KEYS.Unit
  },
  Industry: {
    radarName: 'Industry',
    dataKey: FILTER_TYPES_KEYS.Industry
  },
  Organization: {
    radarName: 'Organization',
    dataKey: FILTER_TYPES_KEYS.Organization
  },
  OrganizationVsIndustry: {
    radarName: 'OrganizationVsIndustry',
    dataKey: FILTER_TYPES_KEYS.OrgVsInd
  }
};

export default UnitComparisonRadarChart;
